import React, { useState, useCallback, useEffect, useRef, useId } from 'react';
import { clsx } from 'clsx';
import { useModalA11y } from '@/hooks/useModalA11y';
import type { Ticket, UserRole } from '@/types/api';
import { 
  generateTicketUrls, 
  copyToClipboard, 
  trackShareEvent,
  validateShareUrl 
} from '@/utils/ticketSharing';

interface ShareTicketModalProps {
  ticket: Ticket;
  userRole: UserRole;
  isOpen: boolean;
  onClose: () => void;
  onShare?: (method: string) => void;
}

interface CopyFeedback {
  show: boolean;
  message: string;
  success: boolean;
}

/**
 * Share Ticket Modal Component
 * Provides multiple sharing options with proper permissions and feedback
 */
export const ShareTicketModal: React.FC<ShareTicketModalProps> = ({
  ticket,
  userRole,
  isOpen,
  onClose,
  onShare
}) => {
  const [copyFeedback, setCopyFeedback] = useState<CopyFeedback>({
    show: false,
    message: '',
    success: false
  });
  const modalRef = useRef<HTMLDivElement>(null);
  
  // Generate URLs for sharing
  const urls = generateTicketUrls(ticket);
  
  // Handle copy operations with feedback
  const handleCopyWithFeedback = useCallback(async (text: string, method: string) => {
    if (!validateShareUrl(text) && !text.startsWith('mailto:')) {
      setCopyFeedback({
        show: true,
        message: 'Invalid URL format',
        success: false
      });
      return;
    }

    const result = await copyToClipboard(text);
    setCopyFeedback({
      show: true,
      message: result.message,
      success: result.success
    });
    
    if (result.success) {
      trackShareEvent(method, ticket.ticketNumber || ticket.id, userRole);
      onShare?.(method);
    }
    
    // Clear feedback after 3 seconds
    setTimeout(() => {
      setCopyFeedback(prev => ({ ...prev, show: false }));
    }, 3000);
  }, [ticket, userRole, onShare]);
  
  // Escape, focus containment and focus restoration come from the shared hook
  // (BugHerd #132); only click-outside remains local to this overlay.
  const titleId = useId();
  useModalA11y({ isOpen, onClose, containerRef: modalRef });

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose]);
  
  // Handle direct email sharing
  const handleEmailShare = useCallback(() => {
    window.location.href = urls.email;
    trackShareEvent('email', ticket.ticketNumber || ticket.id, userRole);
    onShare?.('email');
    onClose();
  }, [urls.email, ticket, userRole, onShare, onClose]);
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div 
        ref={modalRef}
        className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby="share-modal-description"
        tabIndex={-1}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div>
            <h3 id={titleId} className="text-lg font-semibold text-gray-900">
              Share Ticket
            </h3>
            <p id="share-modal-description" className="text-sm text-gray-500 mt-1">
              Share ticket #{ticket.ticketNumber || ticket.id} with others
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 rounded-md p-1"
            aria-label="Close modal"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        {/* Share Options */}
        <div className="p-4 space-y-3">
          {/* Copy Internal Link */}
          <button
            onClick={() => handleCopyWithFeedback(urls.internal, 'internal_link')}
            className="w-full flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-colors"
          >
            <div className="flex-shrink-0">
              <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                  d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
            </div>
            <div className="flex-1 text-left">
              <div className="text-sm font-medium text-gray-900">Copy Link</div>
              <div className="text-xs text-gray-500">Copy internal app link to clipboard</div>
            </div>
          </button>
          
          {/* Email Share */}
          <button
            onClick={handleEmailShare}
            className="w-full flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-colors"
          >
            <div className="flex-shrink-0">
              <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                  d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <div className="flex-1 text-left">
              <div className="text-sm font-medium text-gray-900">Share via Email</div>
              <div className="text-xs text-gray-500">Open email client with ticket details</div>
            </div>
          </button>
          
          {/* URL Preview */}
          <div className="mt-4 p-3 bg-gray-50 rounded-lg">
            <div className="text-xs text-gray-600 mb-1">Internal Link Preview:</div>
            <div className="text-xs text-gray-800 font-mono break-all bg-white p-2 rounded border">
              {urls.internal}
            </div>
          </div>
        </div>
        
        {/* Copy Feedback */}
        {copyFeedback.show && (
          <div className={clsx(
            'absolute top-4 right-16 px-3 py-2 rounded-md shadow-lg text-xs font-medium transition-opacity',
            copyFeedback.success 
              ? 'bg-green-100 text-green-800 border border-green-200'
              : 'bg-red-100 text-red-800 border border-red-200'
          )}>
            <div className="flex items-center space-x-1">
              {copyFeedback.success ? (
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              )}
              <span>{copyFeedback.message}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};